// RD-LUI-003 / RD-LUI-004 — start a tenant role's dashboard load the moment 2FA is
// VERIFIED (while the sign-in exit animation plays) instead of after navigation.
// One single-use slot, in memory only, never persisted. The consumer
// (each role's dashboard hook, INITIAL load only) takes it by an exact key and otherwise
// loads for itself, so a miss is always safe.
//
// Security shape: the slot is keyed by user, tenant, role, aal, kind, year and
// variant (field roles: the price-visibility table set, so a late flip MISSES); it
// only STARTS when the live session is aal2 for that user AND the mirrored tenant
// role equals the role (so the services pick the same table/view fork — the
// operations role stays money-blind); it is dropped on sign-out
// (clearPerUserDeviceState), is single-use, and expires after 15 s.
import { supabase } from '../../supabase';
import { readSessionAal } from '../auth/readSessionAal';
import { getCurrentTenantRole } from '../auth/currentTenantRoleStore';

export const SIGN_IN_PREFETCH_MAX_AGE_MS = 15000;
export const SIGN_IN_PREFETCH_ROLES = ['admin', 'operations', 'billing', 'supervisor', 'worker'];

let slot = null; // { key, promise, startedAt, failed, timer }
let epoch = 0; // bumped by clearSignInPrefetch: a pre-sign-out start stores nothing

export function buildSignInPrefetchKey({
  userId, tenantId, role, aal, kind, year, includePriorYear, variant = '',
}) {
  return [userId, tenantId, role, aal, kind, year, includePriorYear ? 1 : 0, variant].join('|');
}

/** True when a slot exists (cheap peek, so callers skip async work otherwise). */
export function hasSignInPrefetch() {
  return slot !== null;
}

/**
 * Start the prefetch. Resolves true if it started, false if skipped. `run()`
 * returns the fetch promise; the stored promise resolves `{ value, finishedAt }`.
 */
export async function startSignInPrefetch({
  kind, userId, tenantId, role, aal, year, includePriorYear, variant = '', run, client = supabase,
}) {
  if (!userId || !tenantId || typeof run !== 'function') return false;
  if (!SIGN_IN_PREFETCH_ROLES.includes(role) || aal !== 'aal2') return false;
  const startEpoch = epoch;
  const live = await readSessionAal(client);
  if (startEpoch !== epoch) return false; // signed out while checking
  if (!live || live.aal !== 'aal2' || live.userId !== userId) return false;
  if (getCurrentTenantRole() !== role) return false;
  const key = buildSignInPrefetchKey({
    userId, tenantId, role, aal: live.aal, kind, year, includePriorYear, variant,
  });
  const entry = { key, startedAt: Date.now(), failed: false, promise: null };
  entry.promise = Promise.resolve()
    .then(run)
    .then((value) => ({ value, finishedAt: Date.now() }));
  entry.promise.catch(() => { entry.failed = true; }); // never an unhandled rejection
  if (slot) clearTimeout(slot.timer); // a replaced slot's timer never outlives it
  slot = entry;
  // An untaken slot drops itself after the TTL (only if it is still THIS slot).
  entry.timer = setTimeout(() => { if (slot === entry) slot = null; }, SIGN_IN_PREFETCH_MAX_AGE_MS);
  return true;
}

/** Single-use: the promise on an exact key match, young enough, not failed; else null. */
export function takeSignInPrefetch(key) {
  const entry = slot;
  if (!entry) return null;
  if (entry.key !== key) return null; // a different request never consumes the slot
  clearTimeout(entry.timer);
  slot = null;
  if (entry.failed) return null;
  if (Date.now() - entry.startedAt >= SIGN_IN_PREFETCH_MAX_AGE_MS) return null;
  return entry.promise;
}

export function clearSignInPrefetch() {
  epoch += 1;
  if (slot) clearTimeout(slot.timer);
  slot = null;
}
